import { useMemo, useRef, useCallback, type ReactNode } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  type Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DndContext, closestCenter, type DragMoveEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Tag } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { FilterPanelToggle } from 'components/collapsible-filter-panel';
import { DebouncedInput } from 'components/debounced-input';
import { EntityFlagTag } from 'components/entity-flag-tag';
import { OperatorTag } from 'components/operator-tag';
import { PlainLink } from 'components/link';
import { TableActions } from 'components/table/table-actions';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { parseLocalDateTime } from 'utils/dates';
import { EMPTY_CELL } from 'utils/empty-cell';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from 'utils/page-sizes';
import {
  itemRangeText,
  pageRangeText,
} from 'components/search/common/format-helpers';
import { formatCount } from './utils';
import {
  DragHeader,
  DragCell,
  handleDragEnd,
  handleDragMove,
} from 'components/table/draggable';
import type { SearchAtom } from 'atoms/search-atom';
import type {
  EntitySearchResponse,
  EntitySearchResult,
  EntitySearchParams,
} from './types';
import styles from './entity-dashboard.module.scss';

interface EntityTableProps {
  searchResult: SearchAtom<EntitySearchResponse, EntitySearchParams>;
  isFilterOpen: boolean;
  onToggleFilter: () => void;
  filterCount: number;
  onSearchChange: (text: string) => void;
  onSortChange: (
    sortAttribute: string | undefined,
    sortDirection: 'ASC' | 'DESC' | undefined
  ) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  tableId?: string;
  renderRowAction?: (entity: EntitySearchResult) => ReactNode;
  hideFilterToggle?: boolean;
}

const columnHelper = createColumnHelper<EntitySearchResult>();

/**
 * The Entity Library's table: sortable, reorderable columns over the server's
 * entity search, with the filter toggle, search box and pagination.
 * @param {EntityTableProps} props The search, its handlers and table options.
 * @return {JSX.Element} The table.
 */
export function EntityTable({
  searchResult,
  isFilterOpen,
  onToggleFilter,
  filterCount,
  onSearchChange,
  onSortChange,
  onPaginationChange,
  // v2: persisted layouts from before the actions column moved right would
  // override the new order/pinning — new key resets them.
  tableId = 'entity-dashboard-table-v2',
  renderRowAction,
  hideFilterToggle = false,
}: EntityTableProps) {
  const handleSearchChange = useCallback(
    (value: string | number) => {
      const text = value ? String(value).trim() : '';
      onSearchChange(text);
    },
    [onSearchChange]
  );

  const dataColumns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        // Wider default to fit the name alongside the Deduplicated / flag chips.
        header: 'Entity Name',
        size: 340,
        enableSorting: true,
        cell: (info) => (
          <div className={styles.nameCell}>
            <PlainLink
              href={info.row.original.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.entityLink}
            >
              {info.getValue()}
            </PlainLink>
            {/* Chips flow inline after the name and wrap to the next line as a
                unit when the column is too narrow — before the name itself wraps. */}
            <span className={styles.nameChips}>
              {info.row.original.portfolioInvestment && (
                <Tag type="teal" size="sm" className={styles.nameChip}>
                  Portfolio
                </Tag>
              )}
              {info.row.original.deduplicatedAt && (
                <Tag type="gray" size="sm" className={styles.nameChip}>
                  Deduplicated
                </Tag>
              )}
              {info.row.original.operator && (
                <OperatorTag className={styles.nameChip} />
              )}
              <EntityFlagTag
                flagCategory={info.row.original.flagCategory}
                flagRationale={info.row.original.flagRationale}
                flagSeverity={info.row.original.flagSeverity}
              />
            </span>
          </div>
        ),
      }),
      columnHelper.accessor('id', {
        id: 'id',
        header: 'Entity ID',
        size: 180,
        enableSorting: true,
      }),
      columnHelper.accessor('entityType', {
        id: 'entityType',
        header: 'Entity Type',
        size: 200,
        enableSorting: true,
      }),
      columnHelper.accessor('entityClassification', {
        id: 'entityClassification',
        header: 'Entity Classification',
        size: 230,
        enableSorting: true,
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Created Date',
        size: 210,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return value ? parseLocalDateTime(value) : EMPTY_CELL;
        },
      }),
      columnHelper.accessor('updatedAt', {
        id: 'updatedAt',
        header: 'Updated Date',
        size: 210,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return value ? parseLocalDateTime(value) : EMPTY_CELL;
        },
      }),
      columnHelper.accessor('aliasCount', {
        id: 'aliasCount',
        header: '# of Aliases',
        size: 190,
        enableSorting: true,
        cell: (info) => formatCount(info.getValue()),
      }),
      columnHelper.accessor('documentCount', {
        id: 'documentCount',
        header: '# of Documents',
        size: 210,
        enableSorting: true,
        cell: (info) => formatCount(info.getValue()),
      }),
      columnHelper.accessor('capacityCount', {
        id: 'capacityCount',
        header: '# of Capacities',
        size: 210,
        enableSorting: true,
        cell: (info) => formatCount(info.getValue()),
      }),
      columnHelper.accessor('crmAccountCount', {
        id: 'crmAccountCount',
        header: '# of CRM Accounts',
        size: 230,
        enableSorting: true,
        cell: (info) => formatCount(info.getValue()),
      }),
      columnHelper.accessor('crmTractCount', {
        id: 'crmTractCount',
        header: '# of CRM Tracts',
        size: 210,
        enableSorting: true,
        cell: (info) => formatCount(info.getValue()),
      }),
      columnHelper.accessor('titleAssetCount', {
        id: 'titleAssetCount',
        header: '# of Title Assets',
        size: 220,
        enableSorting: true,
        cell: (info) => formatCount(info.getValue()),
      }),
    ],
    []
  );

  const columns = useMemo(() => {
    if (!renderRowAction) {
      return dataColumns;
    }

    const actionColumn = columnHelper.display({
      id: 'actions',
      header: 'Actions',
      // Wide enough for the "Actions" header label (the row cell is just the
      // icon button).
      size: 96,
      enableSorting: false,
      enableResizing: false,
      cell: (info) => renderRowAction(info.row.original),
    });

    // Far right + frozen (PM feedback): the action column trails the data
    // columns and is pinned so it stays visible under horizontal scroll.
    return [...dataColumns, actionColumn];
  }, [dataColumns, renderRowAction]);
  const columnIds = columns.map((col) => col.id as string);

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);
  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder: columnIds,
      initialColumnPinning: {
        left: [],
        right: renderRowAction ? ['actions'] : [],
      },
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const sorting = useMemo(() => {
    const sortAttribute = searchResult.currentPayload?.sortAttribute;
    const sortDirection = searchResult.currentPayload?.sortDirection;

    if (!sortAttribute) return [];

    return [
      {
        id: sortAttribute,
        desc: sortDirection === 'DESC',
      },
    ];
  }, [
    searchResult.currentPayload?.sortAttribute,
    searchResult.currentPayload?.sortDirection,
  ]);

  const table = useReactTable({
    data: searchResult.data?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    columnResizeMode: 'onChange',
    state: {
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;

      if (newSorting.length === 0) {
        onSortChange(undefined, undefined);
      } else {
        const sort = newSorting[0];
        onSortChange(sort.id, sort.desc ? 'DESC' : 'ASC');
      }
    },
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableColumnFilters: false,
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 20,
    scrollPaddingStart: 48,
    initialOffset: 0,
  });

  return (
    <div className={styles.tableContainer}>
      <div className={styles.stackTableArea} ref={parentRef}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(e) =>
            handleDragEnd(
              e,
              columnPinning,
              columnOrder,
              setColumnOrder,
              setColumnPinning,
              hoveredColumnIdRef
            )
          }
          modifiers={[restrictToHorizontalAxis]}
          onDragMove={onDragMove}
        >
          <div className={styles.tableContext}>
            {!hideFilterToggle && (
              <FilterPanelToggle
                isOpen={isFilterOpen}
                filterCount={filterCount}
                onToggle={onToggleFilter}
              />
            )}
            <div className={styles.stackSearchArea}>
              <DebouncedInput
                className={styles.stackSearchField}
                value={searchResult.currentPayload?.text || ''}
                onChange={handleSearchChange}
                placeholder="Search by Entity ID or Name"
              />
            </div>
            <TableActions
              data-testid="entity-table-actions"
              table={table}
              isColumnFilterActive={false}
              setIsColumnFilterActive={() => {}}
              hideColumnFilterButton={true}
            />
          </div>

          <table className={styles.stackTable}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <DragHeader
                        key={header.id}
                        header={header}
                        hoveredColumnIdRef={hoveredColumnIdRef}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody
              style={{
                minHeight: `${virtualizer.getTotalSize()}px`,
              }}
            >
              {searchResult.loading ? (
                <tr>
                  <td
                    colSpan={columnIds.length}
                    style={{ textAlign: 'center', padding: '2rem' }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnIds.length}
                    style={{ textAlign: 'center', padding: '2rem' }}
                  >
                    No results found
                  </td>
                </tr>
              ) : (
                <>
                  {/* Spacer row instead of per-row translateY: a transform on
                      <tr> makes it a containing block, which breaks the sticky
                      positioning of the pinned (frozen) columns inside it. */}
                  {(virtualizer.getVirtualItems()[0]?.start ?? 0) > 0 && (
                    <tr
                      aria-hidden
                      style={{
                        height: `${virtualizer.getVirtualItems()[0].start}px`,
                      }}
                    />
                  )}
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const row = rows[
                      virtualRow.index
                    ] as Row<EntitySearchResult>;
                    return (
                      <tr
                        key={row.id}
                        style={{
                          minHeight: `${virtualRow.size}px`,
                        }}
                      >
                        <SortableContext
                          items={columnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <DragCell
                              key={cell.id}
                              cell={cell}
                              hoveredColumnIdRef={hoveredColumnIdRef}
                            />
                          ))}
                        </SortableContext>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
      {!searchResult.loading && (
        <Pagination
          page={searchResult.data?.page || 1}
          pageSize={searchResult.data?.pageSize || DEFAULT_PAGE_SIZE}
          pageSizes={PAGE_SIZES}
          totalItems={searchResult.data?.totalCount || 0}
          itemRangeText={itemRangeText}
          pageRangeText={pageRangeText}
          onChange={({ page, pageSize }) => {
            onPaginationChange(page, pageSize);
          }}
        />
      )}
    </div>
  );
}
